import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Pencil, Trash2, Plus, Save, X, Search, AlertCircle } from 'lucide-react';
import { Loader } from '../../components/ui';
import { useToastUIStore } from '../../store/toastUIStore';
import { logger } from '../../utils/logger';

interface AIModelConfig {
  id: string;
  name: string;
  model_type: 'chat' | 'embedding' | 'transcription';
  model_id: string;
  is_active: boolean;
  description: string;
  parameters: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

interface EditingState {
  id: string | null;
  isNew: boolean;
  data: Partial<AIModelConfig>;
}

interface OpenAIModel {
  id: string;
  created: number;
  object: string;
  owned_by: string;
}

export const AIModelConfig: React.FC = () => {
  const [models, setModels] = useState<AIModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openAIModels, setOpenAIModels] = useState<OpenAIModel[]>([]);
  const [loadingOpenAIModels, setLoadingOpenAIModels] = useState(false);
  const [openAIModelsError, setOpenAIModelsError] = useState<string | null>(null);
  const { addToast } = useToastUIStore();

  // Model type options
  const modelTypes = [
    { value: 'chat', label: 'Chat Completion' },
    { value: 'embedding', label: 'Embedding' },
    { value: 'transcription', label: 'Transcription' },
  ];

  // Helper function to get local model list when API is unavailable
  const getLocalModelList = () => {
    const now = Date.now();
    return {
      models: [
        // GPT-4 Models
        { id: 'gpt-4-0125-preview', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-turbo-preview', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-1106-preview', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-vision-preview', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-0613', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-32k', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-4-32k-0613', object: 'model', created: now, owned_by: 'openai' },

        // GPT-3.5 Models
        { id: 'gpt-3.5-turbo', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-3.5-turbo-16k', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-3.5-turbo-1106', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-3.5-turbo-0125', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-3.5-turbo-0613', object: 'model', created: now, owned_by: 'openai' },
        { id: 'gpt-3.5-turbo-16k-0613', object: 'model', created: now, owned_by: 'openai' },

        // Embedding Models
        { id: 'text-embedding-ada-002', object: 'model', created: now, owned_by: 'openai' },
        { id: 'text-embedding-3-small', object: 'model', created: now, owned_by: 'openai' },
        { id: 'text-embedding-3-large', object: 'model', created: now, owned_by: 'openai' },

        // Audio/Transcription Models
        { id: 'whisper-1', object: 'model', created: now, owned_by: 'openai' },
        { id: 'tts-1', object: 'model', created: now, owned_by: 'openai' },
        { id: 'tts-1-hd', object: 'model', created: now, owned_by: 'openai' },
      ],
      fallback: true,
      error: 'Using local model list (Netlify function unavailable)',
    };
  };

  // Function to fetch available OpenAI models
  const fetchOpenAIModels = async () => {
    try {
      setLoadingOpenAIModels(true);
      setOpenAIModelsError(null);

      logger.ai('Fetching OpenAI models from Netlify function');

      let data;
      try {
        // Try to fetch from Netlify function
        const response = await fetch('/.netlify/functions/openai-models', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch models: ${response.statusText} (${response.status})`);
        }

        // Get the text of the response first to check if it's valid JSON
        const responseText = await response.text();
        logger.ai('Raw response from models API', {
          preview: responseText.substring(0, 200), // Log first 200 chars
          length: responseText.length,
        });

        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          logger.error('JSON parsing error:', jsonError);
          // If we got HTML instead of JSON, it's likely a 404 or server error
          if (responseText.includes('<!DOCTYPE html>') || responseText.includes('<html>')) {
            throw new Error('Received HTML instead of JSON - function may not be deployed');
          } else {
            throw new Error(`Invalid JSON response: ${jsonError.message}`);
          }
        }
      } catch (fetchError) {
        logger.error('Could not fetch from Netlify function, using local model list:', fetchError);
        // If Netlify function isn't available, use a hardcoded list
        data = getLocalModelList();
      }

      logger.ai('Response from Netlify function', {
        modelCount: data.models?.length || 0,
        fallback: !!data.fallback,
      });

      // Check if there was an error from the function but still returned models
      if (data.error) {
        logger.error('OpenAI models warning:', data.error);
        setOpenAIModelsError(`Note: ${data.error}`);
      }

      // If we have models in the response, use them
      if (data.models && Array.isArray(data.models) && data.models.length > 0) {
        logger.ai(`Received models from OpenAI API`, {
          count: data.models.length,
          examples: data.models.slice(0, 3).map(m => m.id),
        });
        setOpenAIModels(data.models);
      } else {
        // If no models or empty array, throw an error to trigger the fallback
        logger.error('No models returned from Netlify function');
        throw new Error('Failed to retrieve models from Netlify function');
      }
    } catch (err) {
      logger.error('Error fetching OpenAI models:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch OpenAI models';
      logger.error('Full error details:', { message: errorMessage });

      // Check for specific error types
      if (
        errorMessage.includes('HTML') ||
        errorMessage.includes('<!DOCTYPE') ||
        errorMessage.includes('function may not be deployed')
      ) {
        setOpenAIModelsError(
          'OpenAI models function is not deployed yet. Using fallback model list instead.'
        );
      } else if (
        errorMessage.includes('FunctionRelError') ||
        errorMessage.includes('Cannot find function') ||
        errorMessage.includes('404')
      ) {
        setOpenAIModelsError(
          'OpenAI models function could not be found. Using fallback model list instead.'
        );
      } else if (errorMessage.includes('authorize') || errorMessage.includes('401')) {
        setOpenAIModelsError(
          'Authentication error when fetching models. Using fallback model list instead.'
        );
      } else {
        setOpenAIModelsError(`${errorMessage}. Using fallback model list instead.`);
      }

      // Get local model list
      const localModelData = getLocalModelList();
      setOpenAIModels(localModelData.models);
    } finally {
      setLoadingOpenAIModels(false);
    }
  };

  useEffect(() => {
    fetchModels();
    // Always fetch OpenAI models on component load
    fetchOpenAIModels();
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);

      logger.ai('Fetching AI model configurations');
      const { data, error } = await supabase.rpc('get_ai_model_configs');

      if (error) {
        logger.error('Error fetching AI model configurations:', error);
        throw error;
      }

      logger.ai('AI model configurations received', {
        count: data?.length || 0,
        models:
          data?.map(m => ({ type: m.model_type, model: m.model_id, active: m.is_active })) || [],
      });
      setModels(data || []);
    } catch (err) {
      logger.error('Error fetching AI model configurations:', err);
      let errorMessage = 'Failed to fetch AI model configurations';

      if (err instanceof Error) {
        errorMessage = err.message;

        // Handle specific PostgreSQL errors
        if (errorMessage.includes('42702')) {
          errorMessage =
            'Database configuration issue: Column reference is ambiguous. Please run the fix_ai_model_ambiguous_references migration.';
        } else if (errorMessage.includes('permission denied')) {
          errorMessage = 'Permission denied: Your account does not have admin privileges.';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (model: AIModelConfig) => {
    setEditing({
      id: model.id,
      isNew: false,
      data: { ...model },
    });
  };

  const handleAdd = (modelType: 'chat' | 'embedding' | 'transcription') => {
    // Find a good default model for this type from OpenAI models
    let defaultModel = '';

    if (openAIModels.length > 0) {
      if (modelType === 'chat') {
        // Prefer newer GPT-4 models
        const gpt4Model = openAIModels.find(
          m => m.id.includes('gpt-4') && m.id.includes('preview')
        );
        defaultModel = gpt4Model?.id || 'gpt-4-0125-preview';
      } else if (modelType === 'embedding') {
        // Prefer newer embedding models
        const embeddingModel = openAIModels.find(m => m.id.includes('text-embedding-3'));
        defaultModel = embeddingModel?.id || 'text-embedding-ada-002';
      } else if (modelType === 'transcription') {
        defaultModel = 'whisper-1';
      }
    }

    setEditing({
      id: null,
      isNew: true,
      data: {
        name: `New ${modelType.charAt(0).toUpperCase() + modelType.slice(1)} Model`,
        model_type: modelType,
        model_id: defaultModel,
        is_active: false,
        description: '',
        parameters: modelType === 'chat' ? { temperature: 0.7, max_tokens: 1500 } : {},
      },
    });
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleInputChange = (field: string, value: unknown) => {
    if (!editing) return;

    setEditing({
      ...editing,
      data: {
        ...editing.data,
        [field]: value,
      },
    });
  };

  const handleParameterChange = (key: string, value: unknown) => {
    if (!editing) return;

    setEditing({
      ...editing,
      data: {
        ...editing.data,
        parameters: {
          ...(editing.data.parameters || {}),
          [key]: value,
        },
      },
    });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this model configuration?')) {
      return;
    }

    try {
      // Get model details before deletion for logging
      const modelToDelete = models.find(m => m.id === id);

      if (modelToDelete) {
        logger.ai('Deleting AI model config', {
          id,
          name: modelToDelete.name,
          type: modelToDelete.model_type,
          model: modelToDelete.model_id,
        });
      }

      const { data, error } = await supabase.rpc('delete_ai_model_config', {
        p_id: id,
      });

      if (error || (data && data.error)) {
        throw new Error(error?.message || data.error);
      }

      logger.ai('AI model config deleted successfully', {
        id,
        result: data,
      });

      addToast({
        type: 'success',
        message: 'Model configuration deleted successfully',
      });
      await fetchModels();
    } catch (err) {
      logger.error('Error deleting model configuration:', err);
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Error deleting model configuration',
      });
    }
  };

  const handleSave = async () => {
    if (!editing) return;

    try {
      const { id, isNew, data } = editing;
      let result;

      logger.ai(`${isNew ? 'Creating' : 'Updating'} AI model config`, {
        name: data.name,
        type: data.model_type,
        model: data.model_id,
        active: data.is_active,
        parameters: data.parameters,
      });

      if (isNew) {
        result = await supabase.rpc('add_ai_model_config', {
          p_name: data.name,
          p_model_type: data.model_type,
          p_model_id: data.model_id,
          p_is_active: data.is_active,
          p_description: data.description,
          p_parameters: data.parameters,
        });
      } else {
        result = await supabase.rpc('update_ai_model_config', {
          p_id: id,
          p_name: data.name,
          p_model_id: data.model_id,
          p_is_active: data.is_active,
          p_description: data.description,
          p_parameters: data.parameters,
        });
      }

      if (result.error || (result.data && result.data.error)) {
        throw new Error(result.error?.message || result.data.error);
      }

      logger.ai(`AI model config ${isNew ? 'created' : 'updated'} successfully`, result.data);

      addToast({
        type: 'success',
        message: `Model configuration ${isNew ? 'created' : 'updated'} successfully`,
      });
      setEditing(null);
      await fetchModels();
    } catch (err) {
      logger.error('Error saving model configuration:', err);
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Error saving model configuration',
      });
    }
  };

  // Filter models by type for the model editor
  const getModelsForType = (modelType: 'chat' | 'embedding' | 'transcription') => {
    return openAIModels.filter(model => {
      // Simple heuristics for categorizing models
      if (modelType === 'chat') {
        return (
          model.id.includes('gpt-') &&
          !model.id.includes('instruct') &&
          !model.id.includes('embedding')
        );
      } else if (modelType === 'embedding') {
        return model.id.includes('embedding') || model.id.includes('ada');
      } else if (modelType === 'transcription') {
        return model.id.includes('whisper') || model.id.includes('tts');
      }
      return false;
    });
  };

  if (loading && models.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader color="text-blue-500" />
      </div>
    );
  }

  const modelsByType = {
    chat: models.filter(m => m.model_type === 'chat'),
    embedding: models.filter(m => m.model_type === 'embedding'),
    transcription: models.filter(m => m.model_type === 'transcription'),
  };

  return (
    <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 mb-6">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-white mb-4">AI Model Configuration</h2>

        {error && (
          <div className="bg-red-900/20 border border-red-500 text-red-400 p-4 rounded-lg mb-6">
            <div className="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium mb-1">Error loading AI model configurations</p>
                <p>{error}</p>
                {error.includes('migration') && (
                  <p className="mt-2 text-sm">
                    Please contact your administrator to run the database migration script.
                  </p>
                )}
                {error.includes('permission denied') && (
                  <p className="mt-2 text-sm">You need admin privileges to access this section.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {editing ? (
          <div className="bg-gray-900 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-medium text-white mb-4">
              {editing.isNew ? 'Add New Model' : 'Edit Model'}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                  <input
                    type="text"
                    value={editing.data.name || ''}
                    onChange={e => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Model Type</label>
                  <select
                    value={editing.data.model_type || 'chat'}
                    onChange={e => handleInputChange('model_type', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!editing.isNew}
                  >
                    {modelTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Model ID</label>

                  {loadingOpenAIModels ? (
                    <div className="flex items-center justify-center p-4 bg-gray-700 border border-gray-600 rounded-md">
                      <Loader size="small" color="text-blue-500" className="mr-2" />
                      <span className="text-gray-300">Loading models...</span>
                    </div>
                  ) : openAIModelsError ? (
                    <div className="p-3 text-sm text-orange-400 bg-orange-900/20 border border-orange-600 rounded-md mb-2">
                      <AlertCircle size={16} className="inline-block mr-1" />
                      {openAIModelsError}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          placeholder="Search models..."
                          className="w-full px-3 py-2 pl-9 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                      </div>

                      <div className="max-h-60 overflow-y-auto bg-gray-700 border border-gray-600 rounded-md">
                        {editing.data.model_type &&
                          getModelsForType(
                            editing.data.model_type as 'chat' | 'embedding' | 'transcription'
                          )
                            .filter(model =>
                              model.id.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map(model => (
                              <div
                                key={model.id}
                                className={`flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-gray-600/50 ${
                                  editing.data.model_id === model.id
                                    ? 'bg-blue-600/20 border-l-4 border-blue-500 pl-2'
                                    : ''
                                }`}
                                onClick={() => handleInputChange('model_id', model.id)}
                              >
                                <div className="font-mono text-sm truncate">{model.id}</div>
                                {editing.data.model_id === model.id && (
                                  <div className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                                    Selected
                                  </div>
                                )}
                              </div>
                            ))}

                        {editing.data.model_type &&
                          getModelsForType(
                            editing.data.model_type as 'chat' | 'embedding' | 'transcription'
                          ).filter(model =>
                            model.id.toLowerCase().includes(searchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="p-3 text-center text-gray-400">
                              No models found matching "{searchTerm}"
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                  <div className="bg-gray-700 border border-gray-600 rounded-md p-3">
                    <div
                      className="inline-flex items-center cursor-pointer"
                      onClick={() => handleInputChange('is_active', !editing.data.is_active)}
                    >
                      <div
                        className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${editing.data.is_active ? 'bg-green-600' : 'bg-gray-600'}`}
                      >
                        <div
                          className={`absolute top-0.5 bg-white w-5 h-5 rounded-full transition-all duration-300 transform ${
                            editing.data.is_active ? 'left-[calc(100%-21px)]' : 'left-0.5'
                          }`}
                        />
                      </div>
                      <span
                        className={`ml-3 text-sm font-medium ${editing.data.is_active ? 'text-green-400' : 'text-gray-300'}`}
                      >
                        {editing.data.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {editing.data.is_active === true && (
                      <p className="text-xs text-green-400/80 mt-2">
                        This will deactivate any other {editing.data.model_type} models
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  value={editing.data.description || ''}
                  onChange={e => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                />
              </div>

              {editing.data.model_type === 'chat' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Temperature
                    </label>
                    <input
                      type="number"
                      value={editing.data.parameters?.temperature || 0.7}
                      onChange={e =>
                        handleParameterChange('temperature', parseFloat(e.target.value))
                      }
                      min="0"
                      max="2"
                      step="0.1"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Controls randomness (0-2, lower = more deterministic)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      value={editing.data.parameters?.max_tokens || 1500}
                      onChange={e => handleParameterChange('max_tokens', parseInt(e.target.value))}
                      min="100"
                      max="4000"
                      step="100"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Maximum length of generated text</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <X size={16} className="inline mr-1" /> Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Save size={16} className="inline mr-1" /> Save
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Chat Models */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Chat Models</h3>
            <button
              onClick={() => handleAdd('chat')}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
            >
              <Plus size={16} className="mr-1" /> Add Model
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm uppercase">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Model ID</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Temperature</th>
                  <th className="px-4 py-2">Max Tokens</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modelsByType.chat.length > 0 ? (
                  modelsByType.chat.map(model => (
                    <tr key={model.id} className="border-t border-gray-700">
                      <td className="px-4 py-3">{model.name}</td>
                      <td className="px-4 py-3 font-mono text-sm">{model.model_id}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${model.is_active ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'}`}
                        >
                          {model.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">{model.parameters?.temperature || 0.7}</td>
                      <td className="px-4 py-3">{model.parameters?.max_tokens || 1500}</td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(model)}
                            className="p-1 text-blue-400 hover:text-blue-300"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(model.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Delete"
                            disabled={model.is_active && modelsByType.chat.length <= 1}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-center text-gray-400">
                      No chat models configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Embedding Models */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Embedding Models</h3>
            <button
              onClick={() => handleAdd('embedding')}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
            >
              <Plus size={16} className="mr-1" /> Add Model
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm uppercase">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Model ID</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modelsByType.embedding.length > 0 ? (
                  modelsByType.embedding.map(model => (
                    <tr key={model.id} className="border-t border-gray-700">
                      <td className="px-4 py-3">{model.name}</td>
                      <td className="px-4 py-3 font-mono text-sm">{model.model_id}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${model.is_active ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'}`}
                        >
                          {model.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate">{model.description}</td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(model)}
                            className="p-1 text-blue-400 hover:text-blue-300"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(model.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Delete"
                            disabled={model.is_active && modelsByType.embedding.length <= 1}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-center text-gray-400">
                      No embedding models configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transcription Models */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-white">Transcription Models</h3>
            <button
              onClick={() => handleAdd('transcription')}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
            >
              <Plus size={16} className="mr-1" /> Add Model
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm uppercase">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Model ID</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modelsByType.transcription.length > 0 ? (
                  modelsByType.transcription.map(model => (
                    <tr key={model.id} className="border-t border-gray-700">
                      <td className="px-4 py-3">{model.name}</td>
                      <td className="px-4 py-3 font-mono text-sm">{model.model_id}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${model.is_active ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'}`}
                        >
                          {model.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate">{model.description}</td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(model)}
                            className="p-1 text-blue-400 hover:text-blue-300"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(model.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="Delete"
                            disabled={model.is_active && modelsByType.transcription.length <= 1}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-center text-gray-400">
                      No transcription models configured
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
